import { X } from "lucide-react";
import { useLocation } from "react-router-dom";

import {
  IgAiIcon,
  IgButton,
  IgIconButton,
  IgTooltip,
} from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useExitSettings } from "@src/hooks/useExitSettings";
import { SETTINGS_ROUTES } from "@src/routes/routes";
import { IgPageContentBreadcrumbs } from "@src/utils/IgPageContentBreadcrumbs";
import { IgPageTopBarActions } from "@src/utils/IgPageTopBarActions";

interface IgPageContentTopBarAiChat {
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
}

interface IgPageContentTopBarProps {
  /**
   * Shown only by callers inside the create/edit experience flow
   * (`ExperienceStickyHeader`) — replaces the old "Build tests faster with
   * AI" alpha banner with a persistent sparkle icon in the top bar.
   */
  aiChat?: IgPageContentTopBarAiChat;
}

export const IgPageContentTopBar = ({ aiChat }: IgPageContentTopBarProps) => {
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const handleExitSettings = useExitSettings();
  const theme = useTheme();

  const isSettingsPage = location.pathname.startsWith(SETTINGS_ROUTES.Settings);

  return (
    <Grid
      container
      direction={"row"}
      rowSpacing={4}
      columnSpacing={2}
      alignItems={"center"}
      sx={{
        flexShrink: 0,
        width: "100%",
        ...(isSettingsPage && {
          position: "sticky",
          top: 0,
          zIndex: 1300,
          backgroundColor: theme.igBackgroundColors.baseLightHigh,
        }),
      }}
      justifyContent={"space-between"}
      wrap={"nowrap"}
    >
      <Grid>
        <IgPageContentBreadcrumbs />
      </Grid>
      <Grid
        container
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
        columnSpacing={2}
      >
        {isSettingsPage ? (
          <Grid mr={10}>
            <IgTooltip
              title={
                <Stack alignItems="center">
                  <span>Exit Settings</span>
                  {isAdmin && (
                    <span style={{ color: theme.igPrimitives.gray["A100"] }}>
                      Esc
                    </span>
                  )}
                </Stack>
              }
            >
              <IgButton
                onClick={handleExitSettings}
                endIcon={<X size={16} />}
                variant={"text"}
              >
                Close
              </IgButton>
            </IgTooltip>
          </Grid>
        ) : (
          <>
            {aiChat ? (
              <Grid>
                <IgTooltip title={aiChat.tooltip || "Open Atlas Chat (Beta)"}>
                  <IgIconButton
                    onClick={aiChat.onClick}
                    disabled={aiChat.disabled}
                  >
                    <IgAiIcon surface="none" size={16} aria-label="AI chat" />
                  </IgIconButton>
                </IgTooltip>
              </Grid>
            ) : null}
            <Grid>
              <IgPageTopBarActions />
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};
